import { clientHues } from './variant-hues.js';

// Design handoff catalog, used only until a shared ESP catalog is available.
export const VARIANTS_ESP_FALLBACK = Object.freeze([
  'Mailchimp', 'Campaign Monitor', 'Marketo (V2.0)', 'Marketo (V3.0)',
  'Braze', 'Pardot', 'Pardot Lightning', 'HubSpot — HUBL', 'Klaviyo',
  'HubSpot (COS)', 'Deployteq', 'IBM Watson Campaign', 'iContact', 'PostUp',
  'Oracle Eloqua', 'Exact Target (Content Builder)', 'Salesforce Lightning',
  'Dotdigital', 'SendGrid', 'Constant Contact', 'emfluence', 'ActiveCampaign',
  'VerticalResponse', 'Oracle Responsys', 'ClickDimensions', 'Bronto',
  'Epsilon Harmony', 'Emarsys', 'Cheetahmail', 'SharpSpring',
  'Salesforce Marketing Cloud (SFMC)', 'Adobe Campaign', 'Acoustic Campaign',
  'MyEmma', 'GetResponse', 'Adobe Campaign Classic', 'Iterable', 'Mautic',
  'Mailkit', 'MailCampaigns', 'Web Power', 'Zeta', 'Sign-Up.to', 'TaxiForEmail',
  'Cordial', 'Adestra', 'InfusionSoft', 'Drip', 'MailBlaze', 'MoonMail',
  'Ometria', 'Benchmark', 'Copernica', 'Pinpointe', 'Customer.io', 'Any other',
]);

// Matches server/lib/sabre-profiles.js; the server remains authoritative.
export const PROFILE_SCOPE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
export function variantScopeId(name) {
  return String(name || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 128).replace(/-+$/, '');
}
export const MONTH = 30 * 24 * 3600 * 1000;
export const STATUS = {
  draft: 'Draft', files_staged: 'Files staged', harvesting: 'Harvesting',
  harvest_failed: 'Harvest failed', review: 'Needs review', ready: 'Ready',
  rejected: 'Rejected', archived: 'Archived',
};
export const KINDS = [
  { id: 'shipped_html', label: 'Shipped HTML', short: 'HTML', ex: 'index.html, build.html', hint: 'The dev’s delivered build for this email. Required — everything else is read against it.', accept: '.html,.htm', required: true },
  { id: 'assets', label: 'Assets', short: 'Assets', ex: 'hero.jpg, terms@2x.png', hint: 'Every image the HTML references, sliced art and fonts — from this same build.', accept: '.png,.jpg,.jpeg,.gif,.webp', fixtureAccept: '.png,.jpg,.jpeg,.gif,.webp,.svg,.woff,.woff2,.ttf,.otf' },
  { id: 'design_ref', label: 'Design PDF', short: 'Design', ex: 'flyer.pdf, frame.png', hint: 'The PDF or Figma export the build was made from — the same send, not a sibling.', accept: '.pdf', fixtureAccept: '.pdf,.png,.jpg,.jpeg', required: true },
  { id: 'notes', label: 'Notes', short: 'Notes', ex: 'handoff.txt, rules.md', hint: 'Handoff notes or brand rules written for this email.', accept: '.txt,.md', local: true },
];
export const flattenTree = tree => tree.flatMap(c => c.subs.flatMap(s => s.variants.map(v => ({ ...v, client: c.client, client_id: c.slug, sub_brand: s.sub_brand, sub_brand_id: s.slug }))));
export const treeHues = client => clientHues(client.subs.flatMap(s => s.variants));
export const isMature = (at, now = Date.now()) => !!at && now - Date.parse(at) >= 6 * MONTH;
export function sinceLabel(at, now = Date.now()) {
  if (!at || !Number.isFinite(Date.parse(at))) return 'revision date unavailable';
  const days = Math.max(0, Math.floor((now - Date.parse(at)) / 86400000));
  return days >= 360 ? `${Math.floor(days / 360)}y ago` : days >= 30 ? `${Math.floor(days / 30)}mo ago` : days ? `${days}d ago` : 'today';
}
export function recording(files) {
  const html = files.filter(f => f.kind === 'shipped_html');
  const pdf = files.filter(f => f.kind === 'design_ref');
  return { ok: html.length === 1 && pdf.length === 1, html: html.length, pdf: pdf.length };
}
export function referenceRows(files) {
  const groups = new Map();
  for (const file of files) {
    const g = file.email || 0;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(file);
  }
  const rows = [...groups].sort(([a], [b]) => a - b).map(([g, list], index) => {
    const filled = KINDS.filter(k => list.some(f => f.kind === k.id)).map(k => k.id);
    const html = list.filter(f => f.kind === 'shipped_html');
    return { g, n: index + 1, files: list, filled, hasHtml: !!html.length, ok: html.length === 1 && filled.length >= 3, need: Math.max(0, 3 - filled.length), name: html[0]?.name.replace(/\.html?$/i, ''), dup: html.length > 1 ? html.map(f => f.name) : [] };
  });
  return { rows, total: rows.length, complete: rows.filter(r => r.ok).length, ok: rows.some(r => r.ok) };
}
export function validateFile(files, kind, file, { fixture = false } = {}) {
  const lane = KINDS.find(k => k.id === kind);
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (!lane || !(fixture && lane.fixtureAccept || lane.accept).split(',').includes(ext)) throw new Error(`Invalid file type for ${lane?.label || 'this lane'}.`);
  if (file.size > 50 * 1024 * 1024) throw new Error('Files must be 50 MiB or smaller.');
  if (files.some(f => f.name === file.name)) throw new Error('A file with this name is already staged.');
  if (!fixture && lane.required && files.some(f => f.kind === kind)) throw new Error(`Exactly one ${lane.label} is supported. Remove the staged file first.`);
  const posted = files.filter(f => f.kind !== 'notes');
  if (!lane.local && (posted.length >= 25 || posted.reduce((n, f) => n + f.bytes, file.size) > 256 * 1024 * 1024)) throw new Error('Upload limit: 25 files, 256 MiB total.');
}
export function mapProposalStatus(p) {
  if (p.review?.action === 'reject' || p.status === 'rejected') return 'rejected';
  if (p.review?.action === 'approve' || ['active', 'approved'].includes(p.status)) return 'ready';
  if (['queued', 'running'].includes(p.operation_status)) return 'harvesting';
  if (p.status === 'needs_input' || ['failed', 'cancelled', 'interrupted'].includes(p.operation_status)) return 'harvest_failed';
  if (p.workflow_status === 'submitted' || ['ready_for_review', 'pending', 'reviewed'].includes(p.status)) return 'review';
  return 'draft';
}
export function treeFromRegistry(rows, clientName = id => id) {
  const clients = new Map();
  for (const row of rows) {
    const scope = row.raw?.scope || row.scope || {};
    const slug = scope.client_id || row.client_id || 'unassigned';
    const sub = scope.sub_brand_id || row.sub_brand_id || 'default';
    if (!clients.has(slug)) clients.set(slug, { slug, client: clientName(slug) || slug, subs: [] });
    const client = clients.get(slug);
    let group = client.subs.find(s => s.slug === sub);
    if (!group) { group = { slug: sub, sub_brand: row.sub_brand !== undefined ? row.sub_brand : sub === '_root' ? null : sub, variants: [] }; client.subs.push(group); }
    group.variants.push({ ...row, registered: false });
  }
  return [...clients.values()];
}
export function reportFromProposal(p) {
  const candidate = p.candidate_profile || null;
  const findings = p.reasoning?.findings || [];
  const resolved = p.revision?.resolved_finding_ids || [];
  return {
    proposed_profile: candidate,
    patterns: (p.decision_rows || []).map(r => ({ name: r.semantic_role, detail: r.disposition, confidence: Number(r.confidence_bps) >= 9000 ? 'high' : 'review' })),
    esp_tags: (Array.isArray(p.dialect?.candidate?.tokens) ? p.dialect.candidate.tokens : []).map(t => ({ tag: t.token || t.token_id || String(t), platform: p.scope?.esp_id || '', count: t.count ?? 1 })),
    checklist_seed: [
      ...findings.map(f => ({ id: f.finding_id, text: f.message || f.summary || f.finding_id, blocking: !!f.blocking, finding: true, accepted: resolved.includes(f.finding_id), provenance: 'self_derived' })),
      ...(p.decision_rows || []).map((r, i) => ({ id: `decision:${i}`, text: `${r.semantic_role}: ${r.disposition}`, finding: false, accepted: false, provenance: 'self_derived' })),
    ],
    gold_candidates: (candidate?.source_reference_digests || []).map(d => ({ file: d, provenance: 'dev_gold', last_revised_at: null })),
    viewport_hint: { desktop_px: candidate?.container_width || null, mobile_px: null },
    esp_hint: [],
    terminal_findings: [...(p.terminal_findings || []), ...(p.dialect?.terminal_findings || [])],
  };
}
export function amendment(candidate, checklist, summary) {
  if (!candidate || !summary.trim()) throw new Error('A candidate profile and review summary are required.');
  const fields = ['semantic_role', 'disposition', 'owner', 'implementation_mode', 'renderer_recipe', 'responsive_behavior', 'dark_mode_behavior'];
  return {
    region_policies: (candidate.region_policies || []).map(r => Object.fromEntries(fields.map(k => [k, r[k]]))),
    resolved_finding_ids: checklist.filter(c => c.finding && c.accepted).map(c => c.id),
    summary: summary.trim(),
  };
}
export function permissions(row, seat) {
  const raw = row.raw || {};
  const proposer = raw.requested_by?.user_id;
  const own = !!seat.userId && proposer === seat.userId;
  const submitted = raw.workflow_status === 'submitted' || !!raw.submission || raw.status === 'pending';
  const review = row.status === 'review';
  return {
    edit: seat.start && own && review && !submitted,
    submit: seat.start && own && review && !submitted,
    decide: seat.decide && !!proposer && !own && review && submitted,
    own, submitted,
  };
}
